/**
* VideoPlayerMediator.js
* 
* Created on Sep 26, 2013, 4:41:12 PM
* Changes:
* RF 20141203: added play/pause, some refactoring
* 
* @author Javier Quevedo-Fernandez for Ranj
**/

import { isNull } from "../../../../lib/html5-software-library/src/ranj/util/TypeUtil";
import VideoPlayer from "../../core/video/VideoPlayer";
import VideoPlayerEvent from "../../core/video/VideoPlayerEvent";
import VideoPlayerDispenserHelper from "../../core/video/VideoPlayerDispenserHelper";
import AbstractMediator from "../../core/ui/AbstractMediator";
import GameEvent from "../../core/application/GameEvent";
import DebugEvent from "../debug/DebugEvent";
import SkinFactory from "../../core/ui/SkinFactory";
import SkinKeyRegistry from "../../core/ui/SkinKeyRegistry";
import SystemMessageBuilder from "../../core/system-message/controller/SystemMessageBuilder";
import ScenarioLibrary from "../scenario/ScenarioLibrary";
import NGTAssetLocator from "../base/NGTAssetLocator";
import MediatorManager from "../../core/application/MediatorManager";
import RevisableLanguagePack from "../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import { getURLParameter } from "../../../../lib/html5-software-library/src/ranj/util/URLUtil";

export default class VideoPlayerMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {ScenarioLibrary} scenarioLibrary 
     * @param {NGTAssetLocator} assetLocator 
     * @param {MediatorManager} mediatorManager 
     * @param {RevisableLanguagePack} languagePack 
     */
    constructor(target, dispatcher, scenarioLibrary, assetLocator, mediatorManager, languagePack) {
        super(target, dispatcher);

        this.content = target.content;
        this.contentPlayer = target.contentPlayer;
        this._assetLocator = assetLocator;
        this._scenarioLibrary = scenarioLibrary;
        this._mediatorManager = mediatorManager;
        this._languagePack = languagePack;
    }

    initialize() {
        super.initialize();
        this.registerEventListener(DebugEvent.SKIP_CONTENT, this.handleSkipContent.bind(this));
        this.registerEventListener(GameEvent.PAUSE_GAME, this._handlePauseGame.bind(this));
        this.registerEventListener(GameEvent.RESUME_GAME, this._handleResumeGame.bind(this));

        this._showVideo(VideoPlayerDispenserHelper.videoPlayers.mainVideoPlayer);

        this._pauseVideoPlayerButton = this._mediatorManager.createMediator(SkinFactory.getSkin(SkinKeyRegistry.PAUSE_VIDEO_PLAYER_BUTTON_SKIN), SkinKeyRegistry.PAUSE_VIDEO_PLAYER_BUTTON_SKIN);
        this._pauseVideoPlayerButton.setVideoPlayer(this.videoPlayer);
        this._pauseVideoPlayerButton.setParent(this._target);

        const skipVideoDOMButton = document.getElementById('skipVideoButtonLabel');
        if (skipVideoDOMButton) {
            skipVideoDOMButton.innerText = this._languagePack.getText('video.skipButtonLabel');
        }
    }

    destroy() {
        super.destroy();

        this._pauseVideoPlayerButton.destroy();

        this.content = null;
        this.contentPlayer = null;
        if (!isNull(this.videoPlayer)) {
            this.videoPlayer.destroy();
            this.videoPlayer = null;
        }
    }

    _showVideo(videoClass) {
        const videoURL = this._assetLocator.getLocationForContent(this.content, this._scenarioLibrary.getCurrentScenario().id);
        const subtitlesURL = this._assetLocator.getSubtitleLocationForID(this.content, this._scenarioLibrary.getCurrentScenario().id);
        this.videoPlayer = new VideoPlayer(videoClass);
        this.videoPlayer.autoRetry = false; // Set auto retry to false as we want to show an error message that will trigger the retry
        this.videoPlayer.addEventListener(VideoPlayerEvent.PLAYBACK_FINISHED, this.handleVideoEnded, this);
        this.videoPlayer.addEventListener(VideoPlayerEvent.PLAYBACK_READY, this.handleVideoReady, this);
        this.videoPlayer.addEventListener(VideoPlayerEvent.PLAYBACK_ERROR, this.handleVideoError, this);
        this.videoPlayer.load(videoURL, null, subtitlesURL, true, this.content.getVideoControls());
        if(ranj.DEBUG && getURLParameter('skipvideo') == 1) {
            this.handleSkipContent();
        }
    }

    _skipButtonClickedHandler(event) {
        this.handleSkipContent();
    }

    handleVideoReady() {
        this.videoPlayer.play();
    }

    handleVideoError() {
        const systemMessage = new SystemMessageBuilder()
            .setTitle(this._languagePack.getText('assetLoading.videoLoadError.title'))
            .setDescription(this._languagePack.getText('assetLoading.videoLoadError.description'))
            .addOption({
                text: this._languagePack.getText('assetLoading.videoLoadError.okButtonLabel'),
                callback: () => {
                    this.videoPlayer.reload();
                },
            })
            .build();
        this.showSystemMessage(systemMessage);
    }

    handleSkipContent() {
        this.end();
    }

    handleVideoEnded() {
        this.end();
    }

    end() {
        this.videoPlayer.removeEventListener(VideoPlayerEvent.PLAYBACK_FINISHED, this.handleVideoEnded);
        this.videoPlayer.removeEventListener(VideoPlayerEvent.PLAYBACK_READY, this.handleVideoReady);
        this.videoPlayer.removeEventListener(VideoPlayerEvent.PLAYBACK_ERROR, this.handleVideoError);
        if (this._target.onComplete != null) {
            this._target.onComplete();
        }
    }

    setPaused(value) {
        console.log(`VideoPlayerMediator.setPaused: ${value}`);
        if (value) {
            this.videoPlayer.pause();
        } else {
            this.videoPlayer.resume();
        }
    }

    _handlePauseGame(evt) {
        this.setPaused(true);
    }

    _handleResumeGame(evt) {
        this.setPaused(false);
    }
}